import { useCallback, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "@/services/store";
import { notesManagerActions, notesManagerSelectors } from "@/features/NotesManager/model";
import { NoteEditor } from "@/features/NoteEditor";
import { isNotVoid } from "@/shared/utils";
import {
  useCreateNoteMutation,
  useDeleteFileByIdMutation,
  useGetAllPersonsQuery,
  useLazyGetNoteByIdQuery,
  useUpdateNoteMutation,
} from "@/services/api";
import {
  prepareCreateNotePayload,
  prepareUpdateNotePayload,
} from "@/features/NotesManager/helpers";
import { useAlert } from "@/shared/hooks";
import { getErrorMessage } from "@/services/errors";
import type { Person } from "@/services/api";
import type {
  CreateNoteRequest,
  Note,
  UpdateNoteRequest,
} from "@/services/api/entities/note/types";
import type { NoteEditorFormValues } from "@/features/NoteEditor/model";
import type { TabImproved } from "@/shared/Components";

export const useTabsManager = () => {
  const dispatch = useAppDispatch();

  const activeNote = useAppSelector(notesManagerSelectors.getActiveNote);
  const selectedNotes = useAppSelector(notesManagerSelectors.getSelectedNotes);

  const { data: personsResponse } = useGetAllPersonsQuery();

  const [updateNote] = useUpdateNoteMutation();
  const [getNoteById] = useLazyGetNoteByIdQuery();
  const [createNote] = useCreateNoteMutation();
  const [deleteNoteFile] = useDeleteFileByIdMutation();

  const { callError } = useAlert();

  const upsertNote = useCallback((data: NoteEditorFormValues["currentNote"]) => {
    const isTempNote = data.id?.includes("new-");
    const payload = isTempNote ? prepareCreateNotePayload(data) : prepareUpdateNotePayload(data);
    const promise = isTempNote
      ? createNote(payload as CreateNoteRequest)
      : updateNote(payload as UpdateNoteRequest);

    promise
      .unwrap()
      .then((res) => {
        const person = personsResponse?.persons.find((pers) => pers.id === res.personId);

        if (isTempNote) {
          dispatch(notesManagerActions.updateDraftNoteId({ tempId: data.id!, newId: res.id }));
        }

        dispatch(notesManagerActions.updateActiveNote({ ...res, person: person ?? null }));
      })
      .catch((e) => {
        callError({ text: getErrorMessage(e.code) });
      });
  }, []);

  const handleTabChange = useCallback(
    (noteId: string) => {
      dispatch(notesManagerActions.setActiveNote({ noteId }));
    },
    [dispatch],
  );

  const handleCloseTab = useCallback(
    (noteId: string) => {
      dispatch(notesManagerActions.removeSelectedNote({ noteId }));
    },
    [dispatch],
  );

  const deleteNoteFileById = ({
    file,
    noteId,
  }: {
    file: Note["attachments"][number];
    noteId: string;
  }) => {
    deleteNoteFile({ fileId: file.fileDto.fileId })
      .unwrap()
      .catch((e) => {
        callError({ text: getErrorMessage(e.code) });
      })
      .then(() => {
        getNoteById({ noteId })
          .unwrap()
          .then((note) => {
            dispatch(
              notesManagerActions.updateActiveNote({
                ...note,
                person: personsResponse?.persons.find(
                  (person) => note.personId === person.id,
                ) as Person,
              }),
            );
          });
      });
  };

  const tabs: TabImproved[] = useMemo(() => {
    return selectedNotes.map((note) => ({
      key: note.id,
      label: note.title ?? "",
      children: (
        <NoteEditor
          onFileDelete={(file) => deleteNoteFileById({ file: file, noteId: note.id })}
          currentNote={note}
          onSubmit={upsertNote}
        />
      ),
    }));
  }, [selectedNotes, upsertNote]);

  return {
    tabsManagerState: {
      editorOpen: isNotVoid(selectedNotes),
      tabs,
      activeTab: activeNote?.id ?? "",
    },
    tabsManagerMethods: {
      handleTabChange,
      handleCloseTab,
      deleteNoteFileById,
    },
  };
};
